// JSX port of the companion's renderTasks + buildTaskCardHtml: one list of what is on today,
// escalating task cards. READ-ONLY: cards are NOT tap-to-complete and open NO bottom-sheet (every sheet action is a write).
// Expandable cards (have a description, not completed/CPC/content) toggle their
// description inline via useState. Content + appointment cards render display-only.

import React, { useState, useMemo } from 'react';
import {
  timeAgo, relativeDate, patientActionableTasks, patientCompletedTasks, appointmentWhen, withoutShareInvite,
  visitJoinCta, JOIN_OPENS_BEFORE_MIN,
} from '@/logic/patientHome';
import { templateCodeOf } from '@/logic/task';
import { patientCardTitle } from '@/logic/visits';
import { Icon } from './icons';
import useDemoMode from '../../../hooks/useDemoMode';
import useNow from '../../../hooks/useNow';

function parseCtx(t) {
  const raw = t.context_json;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw || '{}'); } catch { return {}; }
  }
  return raw || {};
}

// The booking GUIDE and the visit card itself. Neither is an Elation-booked appointment in the
// `booking_source` sense, so neither can use the branch above, and both are visits rather than
// the `outreach` chores their category makes them.
const VISIT_TEMPLATES = new Set(['onb_patient_televisit_booking', 'onb_patient_virtual_visit']);

// `code` is the ROW's template_code first: WF69 never writes one into context_json, so a ctx-only
// read gave its visit cards the notification bell.
function domainIconName(domain, ctx, code) {
  if (ctx && ctx.booking_source === 'elation') return 'calendar';
  // The initial-televisit BOOKING guide is a visit, not a chore — but it is not an
  // Elation-booked appointment either, so it cannot use the branch above. Without this
  // it falls to its `outreach` category and renders the notification bell.
  if (VISIT_TEMPLATES.has(code)) return 'video-visit';
  const map = { bp: 'bpm-device', weight: 'body-scale', medication: 'info', education: 'heart-bp', monitoring: 'bpm-device', compliance: 'notification', outreach: 'notification', onboarding: 'sparkle' };
  return map[domain] || 'info';
}

function domainIconClass(domain, ctx, code) {
  if (ctx && ctx.booking_source === 'elation') return 'appointment';
  if (VISIT_TEMPLATES.has(code)) return 'appointment';
  const map = { bp: 'bp', weight: 'weight', medication: 'medication', education: 'education', compliance: 'compliance', outreach: 'outreach' };
  return map[domain] || 'compliance';
}

function ActionIcon({ task, ctx, code }) {
  if (task.status === 'completed') {
    return <div className="acc-task-card__action acc-task-card__action--completed"><Icon name="check-filled" size={16} /></div>;
  }
  if (ctx.booking_source === 'elation') {
    return <div className="acc-task-card__action acc-task-card__action--appointment"><Icon name="calendar" size={16} /></div>;
  }
  // Must sit above the ctx.url branch: the booking guide carries a url and would get the news chip.
  if (code === 'onb_patient_televisit_booking') {
    return <div className="acc-task-card__action acc-task-card__action--appointment"><Icon name="video-visit" size={16} /></div>;
  }
  if (task.clinical_domain === 'education' || ctx.url) {
    return <div className="acc-task-card__action acc-task-card__action--info"><Icon name="news" size={16} /></div>;
  }
  if (task.clinical_domain === 'outreach' || task.clinical_domain === 'compliance') {
    return <div className="acc-task-card__action acc-task-card__action--info"><Icon name="notification" size={16} /></div>;
  }
  const isUrgent = task.priority === 'urgent' || task.status === 'overdue';
  return <div className={`acc-task-card__action ${isUrgent ? 'acc-task-card__action--urgent' : 'acc-task-card__action--info'}`}><Icon name="clock" size={16} /></div>;
}

function taskSubtitle(task, hasExpandableDesc, ctx, now) {
  if (task.status === 'completed') return task.completed_at ? timeAgo(task.completed_at) : 'Done';
  // A visit is somewhere the patient has to BE. "Due tomorrow" does not tell them when — but only
  // a LIVE booking gets the time: a missed or cancelled visit shown as one reads as still upcoming.
  const appt = ctx && typeof ctx.appointment === 'object' && !Array.isArray(ctx.appointment) ? ctx.appointment : null;
  const when = appt ? appointmentWhen(appt.scheduled_at, new Date(now)) : null;
  if (appt?.state === 'missed') return when ? `Missed · ${when}` : 'Missed';
  if (when && appt.state === 'booked') return when;
  if (hasExpandableDesc) return relativeDate(task.due_at);
  if (task.description) return task.description.length > 60 ? `${task.description.substring(0, 60)}…` : task.description;
  return relativeDate(task.due_at);
}

function TaskCard({ task, now }) {
  const [expanded, setExpanded] = useState(false);
  const ctx = parseCtx(task);
  const code = templateCodeOf(task);
  const join = visitJoinCta(task, now);
  const isOverdue = task.status === 'overdue';
  const isCompleted = task.status === 'completed';
  const prioClass = isCompleted ? 'completed' : (isOverdue ? 'overdue' : (task.priority || 'normal'));
  const hasDesc = !!(task.description && task.description.length > 0);
  const isCPC = Array.isArray(ctx.changes_summary) && ctx.changes_summary.length > 0;
  const isAppointment = ctx.booking_source === 'elation';
  const hasUrl = !isCompleted && !!ctx.url;
  const isExpandable = !isCompleted && !isCPC && hasDesc && !hasUrl;

  let subtitle;
  if (isAppointment && !isCompleted) {
    const due = relativeDate(task.due_at);
    subtitle = due ? `Requested by your care team · ${due}` : 'Requested by your care team';
  } else {
    subtitle = taskSubtitle(task, isExpandable, ctx, now);
  }

  return (
    <li
      className={`acc-task-card acc-task-card--${prioClass}${isExpandable ? ' acc-task-card--expandable' : ''}`}
      role="listitem"
      onClick={isExpandable ? () => setExpanded((v) => !v) : undefined}
      style={isExpandable ? { cursor: 'pointer' } : undefined}
    >
      <div className={`acc-task-card__icon acc-task-card__icon--${domainIconClass(task.clinical_domain, ctx, code)}`}>
        <Icon name={domainIconName(task.clinical_domain, ctx, code)} size={18} />
      </div>
      <div className="acc-task-card__body">
        <h3 className="acc-task-card__title">{patientCardTitle(task, code)}</h3>
        <p className="acc-task-card__subtitle">{subtitle}</p>
        {/* Inert on purpose: the patient's real button opens their Zoom room, and nothing inside
            the replica may act for the patient. */}
        {join === 'open' && <span className="acc-task-card__join"><Icon name="video-visit" size={16} />Join video visit</span>}
        {join === 'before' && <p className="acc-task-card__join-note">{`Join opens ${JOIN_OPENS_BEFORE_MIN} minutes before your visit.`}</p>}
        {isExpandable && (
          <p className={`acc-task-card__desc ${expanded ? 'acc-task-card__desc--expanded' : 'acc-task-card__desc--collapsed'}`} aria-hidden={!expanded}>
            {task.description}
          </p>
        )}
      </div>
      <ActionIcon task={task} ctx={ctx} code={code} />
    </li>
  );
}

export default function NextSteps({ tasks, firstName }) {
  const demoMode = useDemoMode();
  const now = useNow();
  const [showAllCompleted, setShowAllCompleted] = useState(false);

  // Shared with the staff "Patient tasks" section (logic/patientHome), except the share
  // invite, which renders as its own card (ShareInviteCard).
  const { allPending, allCompleted } = useMemo(() => {
    const own = withoutShareInvite(tasks);
    return { allPending: patientActionableTasks(own), allCompleted: patientCompletedTasks(own) };
  }, [tasks]);

  const { pending, completed } = useMemo(() => {
    const clock = new Date(now);
    const todayStart = new Date(clock.getFullYear(), clock.getMonth(), clock.getDate());
    const todayEnd = new Date(clock.getFullYear(), clock.getMonth(), clock.getDate(), 23, 59, 59);
    const dueHorizon = new Date(clock.getTime() + 15 * 86400000);
    // The 15-day horizon is WF4's nudge-eligibility window, so a day-0 task due ~14 days out
    // (onb_patient_labs_booking) still shows.
    const p = allPending.filter((t) => {
      if (t.status === 'overdue') return true;
      const avail = t.available_at ? new Date(t.available_at) : todayStart;
      return avail <= todayEnd && (!t.due_at || new Date(t.due_at) <= dueHorizon);
    });
    const c = allCompleted.filter((t) => new Date(t.completed_at || 0) >= todayStart);
    return { pending: p, completed: c };
  }, [allPending, allCompleted, now]);

  const title = firstName
    ? <><span className={demoMode ? 'demo-blur' : undefined}>{firstName}</span>{'’s Next Steps'}</>
    : 'Your Next Steps';
  const visibleCompleted = showAllCompleted ? completed : completed.slice(0, 3);
  const empty = pending.length === 0 && completed.length === 0;

  return (
    <section className="acc-section fade-up" aria-labelledby="ph-tasks-title">
      <div className="acc-section__header">
        <h2 className="acc-section__title" id="ph-tasks-title">
          {title}
          {pending.length > 0 && <span className="acc-section__badge acc-section__badge--count">{pending.length}</span>}
        </h2>
      </div>
      <div>
        {empty ? (
          <div className="acc-empty-state" style={{ padding: '20px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
              <div className="acc-empty-state__icon" style={{ margin: 0, width: 36, height: 36 }}><Icon name="check" size={20} /></div>
              <div style={{ textAlign: 'left' }}>
                <h3 className="acc-empty-state__title" style={{ fontSize: 16 }}>All caught up!</h3>
              </div>
            </div>
          </div>
        ) : (
          <>
            <ul className="acc-task-list" role="list" aria-label="Tasks">
              {pending.map((t) => <TaskCard key={t.task_id} task={t} now={now} />)}
              {visibleCompleted.map((t) => <TaskCard key={t.task_id} task={t} now={now} />)}
            </ul>
            {!showAllCompleted && completed.length > 3 && (
              <button type="button" className="acc-view-all-completed" onClick={() => setShowAllCompleted(true)}>
                View all completed ({completed.length})
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
